import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Leaf,
  User,
  Mail,
  Lock,
  Phone,
  MapPin,
  Loader2,
  ArrowRight,
  Sparkles,
  CheckCircle,
  Heart,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

export default function RegisterBenessere() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    email: "",
    password: "",
    confermaPassword: "",
    cellulare: "",
    citta: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazioni
    if (!formData.nome || !formData.cognome || !formData.email || !formData.password) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    if (formData.password !== formData.confermaPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    
    if (!gdprConsent) {
      toast.error("Devi accettare l'informativa sulla privacy");
      return;
    }

    setLoading(true);
    try {
      // Usa il nuovo endpoint /api/public/register-lead che salva nella collezione leads_potenziali
      const response = await fetch(`${API_URL}/api/public/register-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nome: formData.nome,
          cognome: formData.cognome,
          email: formData.email,
          password: formData.password,
          cellulare: formData.cellulare,
          citta: formData.citta,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        toast.success("Controlla la tua email per confermare la registrazione!");
        // Non effettua redirect automatico - l'utente deve verificare l'email
      } else {
        toast.error(data.detail || "Errore nella registrazione");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center shadow-xl">
          <CardContent className="pt-10 pb-8">
            <div className="w-20 h-20 mx-auto bg-teal-100 rounded-full flex items-center justify-center mb-6">
              <Mail className="w-10 h-10 text-teal-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Controlla la tua Email!</h2>
            <p className="text-slate-600 mb-4">
              Ti abbiamo inviato un'email di conferma all'indirizzo che hai indicato.
            </p>
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-teal-800">
                <strong>Clicca sul link nell'email</strong> per confermare il tuo indirizzo e accedere ai contenuti esclusivi.
              </p>
            </div>
            <p className="text-xs text-slate-500 mb-6">
              Non trovi l'email? Controlla la cartella spam o posta indesiderata.
            </p>
            <Link to="/benessere">
              <Button variant="outline" className="w-full">
                Torna alla Homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 via-white to-cyan-50" data-testid="register-benessere-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/benessere" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif text-lg font-semibold text-slate-800">La Scienza del Benessere</span>
            </Link>
            <Link to="/login">
              <Button variant="outline" size="sm" className="rounded-full">
                Hai già un account? Accedi
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Side - Info */}
          <div className="hidden lg:block">
            <div className="mb-8">
              <Badge className="bg-teal-100 text-teal-700 mb-4">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Accesso Gratuito
              </Badge>
              <h1 className="font-serif text-4xl font-bold text-slate-900 mb-4">
                Unisciti alla Scienza del Benessere
              </h1>
              <p className="text-lg text-slate-600">
                Accedi a contenuti esclusivi su meditazione, respirazione, alimentazione consapevole e gestione dello stress.
              </p>
            </div>

            <div className="space-y-4">
              {[
                { icon: Heart, title: "Articoli Scientifici", desc: "Contenuti basati su ricerche e studi" },
                { icon: Leaf, title: "Tecniche di Benessere", desc: "Guide pratiche passo-passo" },
                { icon: Sparkles, title: "AI Assistente", desc: "Fai domande e ricevi risposte personalizzate" },
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Form */}
          <Card className="shadow-xl border-0">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mb-4">
                <User className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-serif">Registrati Gratuitamente</CardTitle>
              <CardDescription>
                Compila il form per accedere ai contenuti sul benessere
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nome e Cognome */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome">Nome *</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="nome"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        placeholder="Mario"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="cognome">Cognome *</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="cognome"
                        name="cognome"
                        value={formData.cognome}
                        onChange={handleChange}
                        placeholder="Rossi"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="mario.rossi@email.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="confermaPassword">Conferma Password *</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="confermaPassword"
                        name="confermaPassword"
                        type="password"
                        value={formData.confermaPassword}
                        onChange={handleChange}
                        placeholder="••••••••"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Cellulare e Città */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cellulare">Cellulare</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="cellulare"
                        name="cellulare"
                        type="tel"
                        value={formData.cellulare}
                        onChange={handleChange}
                        placeholder="+39 333 1234567"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="citta">Città</Label>
                    <div className="relative mt-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="citta"
                        name="citta"
                        value={formData.citta}
                        onChange={handleChange}
                        placeholder="Milano"
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                {/* GDPR Consent */}
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="gdpr"
                    checked={gdprConsent}
                    onCheckedChange={setGdprConsent}
                    className="mt-0.5"
                  />
                  <Label htmlFor="gdpr" className="text-sm text-slate-600 cursor-pointer">
                    Accetto l'informativa sulla privacy e il trattamento dei miei dati personali secondo il GDPR. *
                  </Label>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-white py-6"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Registrazione in corso...
                    </>
                  ) : (
                    <>
                      Registrati Gratis
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                {/* Login Link */}
                <p className="text-center text-sm text-slate-600">
                  Hai già un account?{" "}
                  <Link to="/login" className="text-teal-600 hover:underline font-medium">
                    Accedi qui
                  </Link>
                </p>

                {/* Professional Registration Link */}
                <div className="pt-4 border-t border-slate-100">
                  <p className="text-center text-xs text-slate-500">
                    Sei un professionista (estetista/parrucchiere)?{" "}
                    <Link to="/diventa-partner" className="text-teal-600 hover:underline font-medium">
                      Diventa Partner
                    </Link>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Leaf className="w-5 h-5 text-teal-400" />
            <span className="font-serif font-semibold">La Scienza del Benessere</span>
          </div>
          <p className="text-slate-400 text-sm">
            Contenuti informativi sul benessere. Non sostituiscono il parere medico.
          </p>
        </div>
      </footer>
    </div>
  );
}

// Badge component inline
const Badge = ({ children, className }) => (
  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${className}`}>
    {children}
  </span>
);
